import clsx from 'clsx'
import { Price } from 'libs/searchspring/components/Price'

import { useBadges } from '@/components/hooks/useBadges'
import { usePromolines } from '@/components/hooks/usePromolines'

import { ResultImage } from 'libs/searchspring/components/ResultImage'
import { ResultsSwatches } from 'libs/searchspring/components/ResultSwatches'
import { useSwatches } from 'libs/searchspring/hooks/useSwatches'
import { useMemo } from 'preact/hooks'
import { useBeaconLogClick } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'

const DEFAULT_OPTIONS = {
  hideSwatches: false,
  hidePromolines: false,
  hideBadges: false
}

export function ResultsCard({ result, className, options = DEFAULT_OPTIONS, dataIndex = '0' }) {
  const [variant] = result.variants
  const {
    hideSwatches = DEFAULT_OPTIONS.hideSwatches,
    hidePromolines = DEFAULT_OPTIONS.hidePromolines,
    hideBadges = DEFAULT_OPTIONS.hideBadges
  } = options
  const { currentResult, updateSwatch } = useSwatches(result)
  const clickLogger = useBeaconLogClick()

  const criteria = {
    tags: currentResult?.tags ?? [],
    collections: currentResult?.collection_handle ?? [],
    products: [currentResult.handle]
  }

  const [firstBadge, secondBadge] = useBadges(criteria)
  const [firstPromoline, secondPromoline] = usePromolines(criteria)

  // Creates URL with collection handle reference if it exists
  const url = useMemo(() => {
    return `/products/${currentResult.handle}`
  }, [currentResult])

  // Analytics handling
  const handleLogClick = () => {
    // Collection is a global pulled from template-search.liquid and collection-main.liquid. Ensure value is set in both places :)
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(currentResult)
    clickLogger(currentResult)
  }

  // This is wrapped in an extra div to support injection from Wishlist KING
  // DON'T REMOVE THAT DIV 🔫
  return (
    <div className={clsx('group/card relative flex', className)} id={`result__${result.handle}`} data-dl-track="select_item" data-dl-product-id={currentResult.id} data-dl-index={dataIndex}>
      <div className="flex flex-col gap-y-0.625 w-full grow">
        <div className="relative w-full overflow-hidden aspect-4/5">
          <a href={url} onClick={handleLogClick} aria-label={currentResult.name}>
            <ResultImage result={currentResult}>
              {!hideBadges && (firstBadge || secondBadge) && (
                <div className="absolute top-0.5 left-0.5 md:top-1 md:left-1 flex flex-col gap-y-0.125 max-w-[80%]">
                  {firstBadge && <Badge badge={firstBadge} />}
                  {secondBadge && <Badge badge={secondBadge} />}
                </div>
              )}
            </ResultImage>
          </a>
        </div>
        <div className='grow flex flex-col gap-y-0.5 px-0.5'>
          {!hideSwatches && <ResultsSwatches result={currentResult} swatches={result.swatches} onSelectSwatch={updateSwatch} />}
          <a href={url} className="grow paragraph-s font-medium link-fade" aria-label={currentResult.name}>
            {currentResult.name}
          </a>
          <Price price={variant.price} compareAtPrice={variant.compare_at_price} className="flex gap-x-0.125" />
          {!hidePromolines && (firstPromoline || secondPromoline) && (
            <div className="flex flex-col gap-0.25">
              {firstPromoline && <Promoline promoline={firstPromoline} />}
              {secondPromoline && <Promoline promoline={secondPromoline} />}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const Promoline = ({ promoline }) => {
  if (!promoline) return null
  return (
    <div className="flex w-fit text-[--text-colour] bg-[--bg-colour] px-0.375 py-0.125"
      style={{ '--text-colour': promoline.text_colour, '--bg-colour': promoline.background_colour || 'transparent' }}>
      <span className="text-xs">{promoline.title}</span>
    </div>
  )
}

const Badge = ({ badge }) => {
  if (!badge) return null
  const TextBadge = () => {
    return (
      <div className={clsx('flex w-fit text-[--text-colour] bg-[--bg-colour]', {
        'px-0.5 py-0.25': badge.background_colour
      })}
        style={{ '--text-colour': badge.text_colour, '--bg-colour': badge.background_colour || 'transparent' }}>
        <span className="text-xs font-semibold">{badge.title}</span>
      </div>
    )
  }

  const ImageBadge = () => (
    <div className="flex size-fit">
      <img src={badge.image} className="w-auto h-auto" alt={badge.title || 'Badge'} />
    </div>
  )
  return (
    badge.image ? <ImageBadge /> : <TextBadge />
  )
}
